import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Settings } from "lucide-react";
import logo from "../assets/image/logo.svg";
import { useAuth } from "../context/authContext";
import apiService from "../actions/apiService";
import NotificationBell from "./NotificationBell";

// Import image icons
import homeIcon from "../assets/icon/homeNew.svg";
import userIcon from "../assets/icon/user.svg";
import walletIcon from "../assets/icon/wallet.svg";
import diamondIcon from "../assets/icon/diamond.svg";
import searchIcon from "../assets/icon/search-sm.svg";
import invitationIcon from "../assets/icon/mail-02.svg";
import manageUsersIcon from "../assets/icon/manage-users.svg";
import logsAndHistoryIcon from "../assets/icon/Logs-and-history.svg";

const NavItem = ({ icon, label, active = false, badge, onClick }) => (
  <button
    onClick={onClick}
    className={[
      "relative inline-flex items-center gap-4 px-4 py-[12.5px] rounded-[25px] text-nav-d transition-colors",
      active ? "bg-eslightBlue text-esblue" : "text-esdarkGray",
    ].join(" ")}
  >
    {typeof icon === "string" ? (
      <img src={icon} alt={label} className="max-w-6 max-h-6 w-6 h-6" />
    ) : (
      <icon size={16} />
    )}
    <span>{label}</span>
    {typeof badge === "number" && (
      <span className="h-5 min-w-5 rounded-full font-semibold justify-center bg-[#E84B39] text-white text-[14px] items-center flex p-1">
        {badge}
      </span>
    )}
  </button>
);

const RoleChip = ({ role }) => (
  <span
    className={`ml-4 inline-flex items-center gap-1 rounded-[50px] border px-[10px] py-1 text-[14px] font-[600] capitalize ${
      role === "buyer"
        ? "bg-[#F5C45180] border-[#F5C451] text-[#BC7D00]"
        : "bg-[#CBECF9] border-[#9BDFF6] text-[#003366]"
    }`}
  >
    {role?.replace("_", " ")}
  </span>
);

const Logo = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const handleLogoClick = () => {
    if (userRole === "buyer") {
      navigate("/buyer");
    } else if (userRole === "seller") {
      navigate("/seller");
    } else if (userRole === "admin") {
      navigate("/admin");
    } else if (userRole === "super_admin") {
      navigate("/super-admin");
    }
  };

  return (
    <div className="flex items-center cursor-pointer" onClick={handleLogoClick}>
      <img src={logo} alt="ProtectPay" className="w-[195px] h-[42px]" />
    </div>
  );
};

const Avatars = ({ role, lastName, firstName }) => (
  <div className="relative flex space-x-1">
    {role ? (
      <img
        src={process.env.REACT_APP_IMAGE_URL + role}
        alt="avatar"
        className="relative h-8 w-8 rounded-full bg-slate-200 grid place-items-center text-slate-600 text-xs font-semibold object-cover"
      />
    ) : (
      <span className="relative h-8 w-8 rounded-full bg-slate-200 grid place-items-center text-slate-600 text-xs font-semibold ring-white">
        {firstName?.charAt(0) + lastName?.charAt(0)}
      </span>
    )}
  </div>
);

const UserBlock = ({ role }) => (
  <div className="hidden items-center sm:flex md:flex lg:hidden min-[1320px]:flex">
    <Avatars
      role={role?.profile_image_url}
      firstName={role.firstName}
      lastName={role.lastName}
    />
    <div className="leading-tight text-left ml-4">
      <div className="text-[14px] text-esdarkGray font-semibold">
        {role?.firstName && role?.lastName && (
          <span className="text-esDarkBlue">
            {role?.firstName} {role?.lastName}
          </span>
        )}
        {!role?.firstName && !role?.lastName && (
          <span className="text-esDarkBlue">N/A</span>
        )}
      </div>
      <Link
        to={"/my-profile"}
        className="text-[12px] text-esdarkGray font-normal opacity-50"
      >
        Go to my profile.
      </Link>
    </div>
  </div>
);

export default function AdminHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, user } = useAuth();
  const [pendingReqCount, setPendingReqCount] = useState(0);

  // Fetch pending requests count for header badge
  useEffect(() => {
    let active = true;
    const fetchCount = async () => {
      if (userRole !== "seller" && userRole !== "buyer") {
        setPendingReqCount(0);
        return;
      }
      try {
        if (userRole === "seller") {
          try {
            const res = await apiService.get("/requests/count", {
              role: userRole,
              status: "pending",
            });
            if (!active) return;
            const count = res?.data?.count ?? res?.data?.data?.count ?? 0;
            setPendingReqCount(Number(count) || 0);
          } catch (_) {
            if (active) setPendingReqCount(0);
          }
        } else if (userRole === "buyer") {
          try {
            const res = await apiService.get("/items/buyer/invitations/count");
            if (!active) return;
            const count = res?.data?.count ?? res?.data?.count ?? 0;
            setPendingReqCount(Number(count) || 0);
          } catch (_) {
            if (active) setPendingReqCount(0);
          }
        }
      } catch (_) {
        setPendingReqCount(0);
      }
    };
    fetchCount();
    return () => {
      active = false;
    };
  }, [userRole]);

  // Build nav items based on role
  const adminNavItems = [
    { icon: homeIcon, label: "Dashboard", path: "/admin" },
    // Per user's mapping: listings -> users, buyers -> transactions, transactions -> reports
    { icon: manageUsersIcon, label: "Manage users", path: "/admin/users" },
    {
      icon: logsAndHistoryIcon,
      label: "Logs and history",
      path: "/admin/reports",
    },
    { icon: walletIcon, label: "Transactions", path: "/admin/transactions" },
  ];

  const superAdminNavItems = [
    { icon: homeIcon, label: "Dashboard", path: "/super-admin" },
    { icon: userIcon, label: "Users", path: "/super-admin/users" },
    {
      icon: walletIcon,
      label: "Transactions",
      path: "/super-admin/transactions",
    },
    { icon: diamondIcon, label: "Admins", path: "/super-admin/admins" },
  ];

  const sellerNavItems = [
    { icon: homeIcon, label: "Dashboard", path: "/seller" },
    { icon: diamondIcon, label: "My listings", path: "/seller/listings" },
    {
      icon: userIcon,
      label: "Buyers Requests",
      path: "/seller/requests",
      badge: Number.isFinite(pendingReqCount) ? pendingReqCount : 0,
    },
    // { icon: walletIcon, label: "Transactions", path: "/seller/transactions" },
  ];

  const buyerNavItems = [
    { icon: homeIcon, label: "Dashboard", path: "/buyer" },
    { icon: searchIcon, label: "Search Items", path: "/buyer/listings" },
    {
      icon: invitationIcon,
      label: "Invitations",
      path: "/buyer/requests",
      badge: Number.isFinite(pendingReqCount) ? pendingReqCount : 0,
    },
    { icon: walletIcon, label: "Transactions", path: "/buyer/transactions" },
  ];

  // Select nav items based on user role
  let navItems;
  if (userRole === "super_admin") {
    navItems = superAdminNavItems;
  } else if (userRole === "admin") {
    navItems = adminNavItems;
  } else if (userRole === "seller") {
    navItems = sellerNavItems;
  } else if (userRole === "buyer") {
    navItems = buyerNavItems;
  } else {
    navItems = adminNavItems; // fallback
  }

  const handleNavClick = (path) => {
    // Only navigate if we're not already on that path
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const isActive = (path) => {
    // For dashboard routes, check exact match
    if (
      path === "/admin" ||
      path === "/super-admin" ||
      path === "/seller" ||
      path === "/buyer"
    ) {
      return location.pathname === path;
    }
    // For sub-routes, check if current path starts with the nav item path
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <div className="w-full bg-bgColor z-[100] sticky top-0">
        <div className="mx-auto w-full max-w-[1642px] px-0 lg:px-4">
          <div className="mt-[0px] lg:mt-[20px] gap-2 flex items-center justify-between rounded-tl-[0px] lg:rounded-tl-[50px] rounded-tr-[0px] lg:rounded-tr-[50px] rounded-br-[50px] rounded-bl-[50px] border border-[#1e1e1e0D] bg-white px-8 py-4">
            {/* Left: Logo + role */}
            <div className="flex items-center">
              <Logo />
              <RoleChip role={userRole} />
            </div>

            {/* Center: Nav */}
            <nav className="hidden items-center gap-4 lg:flex">
              {navItems.map((item) => (
                <NavItem
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  active={isActive(item.path)}
                  badge={item.badge}
                  onClick={() => handleNavClick(item.path)}
                />
              ))}
            </nav>

            {/* Right: User */}
            <div className="flex items-center gap-2">
              <UserBlock role={user} />
              {/* <div className="block sm:hidden">
                <Avatars
                  role={user?.profile_image_url}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                />
              </div> */}
              <NotificationBell />
              <Link
                to="/account-details"
                className=" inline-flex h-[42px] w-[42px] items-center justify-center rounded-[8px] border border-[#1E1E1E0D] hover:bg-slate-50"
              >
                <Settings size={20} className="text-slate-600" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 rounded-tl-[25px] rounded-tr-[25px] right-0 bg-white z-[100] lg:hidden">
        <div className="flex gap-4 justify-center items-center py-4 px-4">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`flex items-center justify-center relative transition-all duration-200 ${
                isActive(item.path)
                  ? "bg-esblue text-white px-3 text-[14px] py-2 rounded-full"
                  : "text-esdarkGray px-3 py-2"
              }`}
            >
              <div className="relative">
                {typeof item.icon === "string" ? (
                  <img
                    src={item.icon}
                    alt={item.label}
                    className={`w-5 h-5 ${
                      isActive(item.path) ? "filter brightness-0 invert" : ""
                    }`}
                  />
                ) : (
                  <item.icon
                    size={20}
                    className={
                      isActive(item.path) ? "text-white" : "text-esdarkGray"
                    }
                  />
                )}
                {Number(item.badge) > 0 && !isActive(item.path) && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {item.badge}
                  </div>
                )}
              </div>
              {isActive(item.path) && (
                <span className="ml-2 text-sm !font-semibold text-white">
                  {item.label}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
